import _get from 'lodash/get';
import { TFunction } from 'i18next';
import { AiDocumentationFieldEnum } from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import FileUploader from '@lib/components/FilesUploader/FileUploader';
import { AttachmentValue } from '@lib/components/FileUploaderUppy/FileUploaderUppy';
import getAttachmentMeta from '@lib/components/DocumentationPreview/utils/getAttachmentMeta';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import { FilesPreview } from '@lib/components/FilesUploader/enums';
import {
  getAttachmentValidation,
  getFieldValidation,
} from '@lib/utils/validationUtils';
import {
  FileSizeLimits,
  UppyDocumentationImportAllowedFileTypes,
  videoSizeLimit,
} from '@lib/enums/mimeTypes';
import { ButtonSizes } from '@lib/components/Button/Button';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import AiFieldAssist from 'routes/Documentation/DocumentForm/components/AiFieldAssist';

export enum VersionFormFieldNames {
  DocumentName = 'documentName',
  Attachment = 'attachmentAttached',
  Notes = 'notes',
  RequireReread = 'requireReread',
}

export default function getVersionFormFields({
  isFullAdmin = true,
  isPlanLocked,
  t,
  isAiEnabled = false,
  isVideoAiIndexingSupported = true,
  aiCredits,
}: {
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  onUpgrade?: () => void;
  t: TFunction<'translation', undefined>;
  // AI auto-fill of the notes field requires the AiAssistance module on the
  // company plan (Write).
  isAiEnabled?: boolean;
  // Whether the company's AI indexing pipeline can index video (region-derived,
  // via CompanyPlan.aiIndexableMediaKinds). Some regions can't index video even
  // when AiAssistance itself is enabled, so a video attachment must still hide
  // the assist affordance.
  isVideoAiIndexingSupported?: boolean;
  // Remaining AI credits shown under the assist row; null/undefined hides it.
  aiCredits?: AiCredits | null;
  // Refetch the AI budget after a generation consumes credits.
}): FieldItem[] {
  // The require-reread checkbox is purely a Full-Admin upgrade affordance
  // when locked — hide it entirely for User-role accounts.
  const hideRequireReread = !!isPlanLocked && !isFullAdmin;
  // A video attachment whose plan region can't index video has nothing for AI
  // to read, so the assist affordance stays hidden regardless of isAiEnabled.
  const isVideoAttachmentUnsupported = (values: Values) =>
    !isVideoAiIndexingSupported &&
    getAttachmentMeta(
      _get(values, [VersionFormFieldNames.Attachment]) as AttachmentValue,
    ).mimeType.startsWith('video');
  // This form only ever creates a new version, so the AI generation source is
  // always the freshly-uploaded attachment (mirrors DocumentForm's create-mode
  // gate — there is no saved version to regenerate from here).
  const isAiAssistVisible = (values: Values) =>
    isAiEnabled &&
    !!_get(values, [VersionFormFieldNames.Attachment, 'file', 'id']) &&
    !isVideoAttachmentUnsupported(values);
  return [
    {
      name: VersionFormFieldNames.DocumentName,
      label: t('document-name'),
      element: Input,
      validation: getFieldValidation({ t }),
      componentProps: {
        disabled: true,
      },
      isOptionalFn: () => false,
    },
    {
      name: VersionFormFieldNames.Attachment,
      label: '',
      validation: getAttachmentValidation({ required: true, t }),
      element: FileUploader,
      componentProps: {
        filesView: FilesPreview.Documents,
        allowedFileTypes: UppyDocumentationImportAllowedFileTypes,
        note: t('documents-import-files-upload-note', {
          sizeImg: FileSizeLimits.Image,
          sizePdf: FileSizeLimits.File,
          sizeVideo: FileSizeLimits.Video,
        }),
        maxFileSize: videoSizeLimit,
        uploadButtonProps: {
          buttonText: t('upload-file'),
          buttonSize: ButtonSizes.large,
          leftIcon: 'upload',
          fullWidth: true,
        },
      },
    },
    {
      name: VersionFormFieldNames.Notes,
      label: t('version-notes'),
      element: TextEditor,
      validation: getFieldValidation({
        required: true,
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
      getComponentProps: (values) =>
        isAiAssistVisible(values)
          ? {
              toolbarExtra: (
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Notes}
                  targetFieldName={VersionFormFieldNames.Notes}
                  creditsRemaining={aiCredits}
                  detectingLabel={t('ai-generating-notes')}
                  labels={{
                    idleLabel: t('ai-generate-with-ai'),
                    idleCta: t('ai-generate-notes-cta'),
                    regenerateCta: t('ai-regenerate'),
                  }}
                />
              ),
            }
          : undefined,
    },
    ...(hideRequireReread
      ? []
      : [
          {
            name: VersionFormFieldNames.RequireReread,
            label: t('require-reread'),
            element: CheckBoxSingleBool,
            componentProps: {
              options: [
                {
                  value: 'true',
                },
              ],
              disabled: isPlanLocked,
              lockedProps: {
                locked: isPlanLocked,
                tooltip: t('upgrade-to-unlock-this-feature'),
              },
            },
          } as FieldItem,
        ]),
  ];
}
